import type {
    BaggageFuelEconomyPayload,
    BaggageFuelEconomyTradeEntry,
    BaggageFuelEconomyVehicleEntry
} from '@makemydeal/dr-dash-bff-types';
import type { FuelSavingApplyPayload, FuelSavingFormValues } from '@makemydeal/dr-activities-fuel-saving';

const parseNumber = (value?: string | null): number | undefined => {
    if (value == null) return undefined;
    const trimmed = String(value).trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Builds a shared entry containing fuelPricePerGallon, milesDriven, and milesPer.
 * fuelPricePerGallon is parsed from the provided price field name.
 * Returns undefined when no input has been populated.
 */
const buildEntry = (
    values: FuelSavingFormValues,
    priceField: 'newVehiclePricePerGallon' | 'tradePricePerGallon'
): BaggageFuelEconomyVehicleEntry | undefined => {
    const fuelPricePerGallon = parseNumber(values[priceField]);
    const milesDriven = parseNumber(values.milesDriven);
    if (fuelPricePerGallon == null && milesDriven == null) return undefined;

    const entry: BaggageFuelEconomyVehicleEntry = {};
    if (fuelPricePerGallon != null) entry.fuelPricePerGallon = fuelPricePerGallon;
    if (milesDriven != null) {
        entry.milesDriven = milesDriven;
        const milesPer = values.milesPer?.toLowerCase();
        if (milesPer) {
            entry.milesPer = milesPer;
        }
    }
    return entry;
};

/**
 * Transforms the apply payload emitted by the fuel-saving activity into the
 * BFF-shaped BaggageFuelEconomyPayload that will be persisted as deal baggage
 * via the saveOffer call.
 */
export const buildBaggageFuelEconomyPayload = (payload: FuelSavingApplyPayload): BaggageFuelEconomyPayload => {
    const result: BaggageFuelEconomyPayload = {};

    const vehiclesWithTrades = payload.allVehicles ?? [];
    const allTrades = vehiclesWithTrades.flatMap((entry) => entry.trades ?? []);

    const vehicles: Record<string, BaggageFuelEconomyVehicleEntry> = {};
    for (const entry of vehiclesWithTrades) {
        const vehicleId = entry.vehicle.id;
        if (!vehicleId) continue;

        // Vehicle entry uses the first trade's form values (shared milesDriven/milesPer inputs).
        // newVehicleMpg is persisted even without price/miles so the form reliably restores
        // the user's entered MPG on reopen rather than falling back to the API-derived value.
        const firstTradeWithValues = entry.trades?.find((trade) => !!trade.values);
        if (!firstTradeWithValues?.values) continue;

        const baseEntry = buildEntry(firstTradeWithValues.values, 'newVehiclePricePerGallon');
        const newVehicleMpg = parseNumber(firstTradeWithValues.values.newVehicleMpg);
        if (!baseEntry && newVehicleMpg == null) continue;

        const vehicleEntry: BaggageFuelEconomyVehicleEntry = baseEntry ?? {};
        vehicleEntry.isPrimary = entry.vehicle.isPrimary;
        if (newVehicleMpg != null) vehicleEntry.newVehicleMpg = newVehicleMpg;
        vehicles[vehicleId] = vehicleEntry;
    }
    if (Object.keys(vehicles).length > 0) {
        result.vehicles = vehicles;
    }

    const trades: Record<string, BaggageFuelEconomyTradeEntry> = {};
    for (const trade of allTrades) {
        if (!trade.vin || !trade.values) continue;
        const tradeEntry: BaggageFuelEconomyTradeEntry | undefined = buildEntry(trade.values, 'tradePricePerGallon');
        if (!tradeEntry) continue;
        const tradeInMpg = parseNumber(trade.values.tradeInMpg);
        if (tradeInMpg != null) tradeEntry.tradeInMpg = tradeInMpg;
        trades[trade.vin] = tradeEntry;
    }
    if (Object.keys(trades).length > 0) {
        result.trades = trades;
    }

    return result;
};
